import { format } from 'date-fns'

import {
  Flex,
  Image,
  Skeleton,
  SkeletonText,
  Text,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import { AudienceBadge } from './AudienceBadge'
import { ChangelogScheduledBadge } from './ChangelogScheduledBadge'
import type { ChangelogEntryData } from './useChangelog'

import styles from './ChangelogEntryCard.module.css'

import type { JSX } from 'react'

interface ChangelogEntryCardProps {
  entry: ChangelogEntryData
  onClick: (id: string) => void
  /** When true, shows the "New" badge on general-audience entries. */
  isNew?: boolean
}

/**
 * Decodes HTML entities using browser's built-in parser
 */
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

/**
 * Strips HTML tags and truncates text to create an excerpt
 */
function createExcerpt(
  html: string | null | undefined,
  maxLength = 120,
): string {
  if (!html) return ''
  // Strip HTML tags and decode entities
  const text = decodeHtmlEntities(
    html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim(),
  )
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength).trim()}...`
}

/**
 * Skeleton component for changelog entry card loading state.
 * Matches the layout of ChangelogEntryCard.
 */
function ChangelogEntryCardSkeleton(): JSX.Element {
  return (
    <div className={styles.card}>
      <Flex gap="md" align="start">
        <Flex direction="column" gap="2xs" className={styles.content}>
          <SkeletonText width={180} />
          <SkeletonText lines={2} />
          <SkeletonText width={80} />
        </Flex>
        <Skeleton width={80} height={60} className={styles.thumbnail} />
      </Flex>
    </div>
  )
}

/**
 * Card component for displaying a changelog entry in a list.
 * Shows thumbnail, title, excerpt, and date.
 */
export function ChangelogEntryCard({
  entry,
  onClick,
  isNew = false,
}: ChangelogEntryCardProps): JSX.Element {
  const formattedDate = format(new Date(entry.publishedAt), 'MMM d, yyyy')
  const excerpt = createExcerpt(entry.content)
  const coverImageUrl =
    typeof entry.coverImageUrl === 'string' ? entry.coverImageUrl : null

  return (
    <div className={styles.card}>
      {/* Stretched link for entire card click area */}
      <UnstyledButton
        onClick={(): void => onClick(entry.id)}
        aria-label={`View changelog: ${entry.title}`}
        className={styles.stretchedLink}
      />

      <Flex gap="md" align="start">
        <Flex direction="column" gap="2xs" className={styles.content}>
          <span className={styles.titleRow}>
            <AudienceBadge
              audience={entry.audience}
              isNew={isNew}
              size="small"
              className={styles.badge}
            />
            <ChangelogScheduledBadge
              publishedAt={entry.publishedAt}
              size="small"
              className={styles.badge}
            />
            <Text weight="bold">{entry.title}</Text>
          </span>
          {excerpt && (
            <Text color="subtle" className={styles.excerpt}>
              {excerpt}
            </Text>
          )}
          <Text size="sm" color="subtle">
            {formattedDate}
          </Text>
        </Flex>
        {coverImageUrl && (
          <Image src={coverImageUrl} alt="" className={styles.thumbnail} />
        )}
      </Flex>
    </div>
  )
}

ChangelogEntryCard.Skeleton = ChangelogEntryCardSkeleton
